<template>
    <div>
        <!-- SSO Information Alert -->
        <v-alert
            v-if="organization_sso_settings && organization_sso_settings.sso_enabled"
            type="info"
            dense
            text
            class="mb-4"
        >
            <strong>Single Sign-On (SSO) Enabled:</strong> Password management and two-factor authentication are handled through your organization's SSO provider.
        </v-alert>

        <v-text-field
            v-model="team_member_search"
            append-icon="mdi-magnify"
            label="Search RedChirp Users"
            single-line
            hide-details
            style="margin-right: 16px; margin-left: 16px; margin-bottom: 5px;"
            v-show="show_search_bar"
            autocomplete="off"
        />

        <m-boolean-search-filter
            style="margin-top: 15px; margin-left: 10px;"
            label="Archived"
            v-model="filter_archived"
        />

        <v-data-table
            style="margin-top: 15px; margin-bottom: 15px;"
            :headers="team_members_headers"
            :items="filtered_employees"
            :items-per-page="100"
            class="elevation-1"
            :hide-default-footer="true"
            disable-sort
            :footer-props="{
                disableItemsPerPage: false,
                disablePagination: false,
            }"
            :search="team_member_search"
        >
            <template v-slot:item.is_system_admin="{ item }">
                <v-simple-checkbox
                    v-model="item.is_system_admin"
                    disabled
                ></v-simple-checkbox>
            </template>

            <template v-slot:item.is_organization_admin="{ item }">
                <v-simple-checkbox
                    v-model="item.is_organization_admin"
                    disabled
                ></v-simple-checkbox>
            </template>

            <template v-slot:item.is_archived="{ item }">
                <v-simple-checkbox
                    v-model="item.is_archived"
                    disabled
                ></v-simple-checkbox>
            </template>

            <template v-slot:item.payment_permissions="{ item }">
                {{ $rc.pretty(item.payment_permissions, 'enum_payment_permissions') }}
            </template>

            <template v-slot:item.bulk_message_permissions="{ item }">
                {{ $rc.pretty(item.bulk_message_permissions, 'enum_bulk_message_permissions') }}
            </template>

            <template v-slot:item.action="{ item }">
                <v-menu
                    bottom
                    offset-y
                >

                    <template v-slot:activator="{ on, attrs }">
                        <v-icon
                            v-bind="attrs"
                            v-on="on"
                        >
                            mdi-dots-vertical-circle-outline
                        </v-icon>
                    </template>

                    <v-list>

                        <v-list-item v-on:click="showProfile(item)">
                            <v-list-item-title>RedChirp User Settings...</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-on:click="signInAs(item)"
                            v-if="is_sys_admin"
                        >
                            <v-list-item-title>Sign In As...</v-list-item-title>
                        </v-list-item>

                        <v-divider />

                        <v-list-item
                            v-for="(endpoint, index) in getEmployeeEndpoints(item)"
                            :key="index"
                            v-on:click="viewConversations($event, item, endpoint)"
                        >
                            <v-list-item-title>View Conversations: {{ endpoint.label }}...</v-list-item-title>
                        </v-list-item>

                        <v-divider v-show="can_write" />

                        <!--Right now the "can_write" field is driven by the org admin setting. That _could_ change in the future and at that point we should update this.-->
                        <m-restricted-menu-list-item
                            v-if="can_write"
                            :disabled="!hasAccessToPasswordManagement(item)"
                            v-on:click="sendPasswordResetLink(item)"
                            text="Send setup account / reset password link..."
                            :tooltip_text="getPasswordResetTooltip(item)"
                        />

                        <m-restricted-menu-list-item
                            v-show="can_write"
                            :disabled="!hasAccessToPasswordManagement(item)"
                            v-on:click="showSetPasswordDialog(item)"
                            text="Set Password..."
                            
                            
                            :tooltip_text="getSetPasswordTooltip(item)"
                        />

                        <v-list-item
                            v-on:click="clear2FA(item)"
                            v-if="$rc.$state.hasRedChirpStaffRoleAdministrator() && !isUserForcedToUseSSO(item)"
                        >
                            <v-list-item-title>Reset Two Factor Authentication (2FA)...</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-on:click="onClickSendWelcomeEmail(item)"
                            v-if="$rc.$state.isAuthActorSystemAdmin()"
                        >
                            <v-list-item-title>Draft Welcome Email...</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-on:click="onClickSendWelcomeEmailOrgAdmin(item)"
                            v-if="$rc.$state.isAuthActorSystemAdmin() && item.is_organization_admin"
                        >
                            <v-list-item-title>Draft Welcome Email (Org Admin Setting up Org)...</v-list-item-title>
                        </v-list-item>

                        <v-divider v-show="can_write" />

                        <m-restricted-menu-list-item
                            v-on:click="showSetBulkMessagePermissionsDialog(item)"
                            :disabled="!($rc.$state.isOrganizationAdmin() || $rc.$state.isAuthActorSystemAdmin())"
                            v-show="can_write"
                            text="Set Bulk Message Permissions..."
                            :tooltip_text="!$rc.$state.isAuthActorSystemAdmin() ? 'Org Admin Only' : 'Org Admin & Staff Only'"
                        />

                        <m-restricted-menu-list-item
                            v-on:click="showSetPaymentPermissionsDialog(item)"
                            :disabled="!($rc.$state.isOrganizationAdmin() || $rc.$state.isAuthActorSystemAdmin())"
                            v-show="can_write"
                            text="Set Payment Permissions..."
                            :tooltip_text="!$rc.$state.isAuthActorSystemAdmin() ? 'Org Admin Only' : 'Org Admin & Staff Only'"
                        />

                        <v-list-item
                            v-on:click="toggleArchived(item)"
                            v-show="can_write"
                        >
                            <v-list-item-title>{{ getToggleArchiveLabel(item) }}</v-list-item-title>
                        </v-list-item>

                        <v-list-item
                            v-on:click="toggleOrganizationAdmin(item)"
                            v-show="can_write"
                        >
                            <v-list-item-title>{{ getToggleOrganizationAdminLabel(item) }}</v-list-item-title>
                        </v-list-item>

                        <v-divider v-show="$rc.$state.isAuthActorSystemAdmin()" />

                        <m-restricted-menu-list-item
                            v-on:click="onClickSetBillingSection(item)"
                            v-if="$rc.$state.isAuthActorSystemAdmin()"
                            text="Billing Section..."
                        />

                        <v-divider v-show="is_sys_admin" />

                        <m-restricted-menu-list-item
                            v-on:click="toggleSystemAdmin(item)"
                            v-if="is_sys_admin"
                            :disabled="!$rc.$state.hasRedChirpStaffRoleAdministrator()"
                            :text="getToggleSystemAdminLabel(item)"
                            :tooltip_text="!$rc.$state.hasRedChirpStaffRoleAdministrator() ? 'Only RedChirp staff with Administrator role can modify system administrator privileges.' : 'Staff Only'"
                        />


                    </v-list>
                </v-menu>


            </template>


        </v-data-table>

        <ul>
            <li
                v-for="(item, index) in users_that_can_access_other_orgs"
                :key="index"
                v-html="item"
                style="font-weight: bold; color: red"
            />
        </ul>

        <ul>
            <li
                v-for="(item, index) in users_forced_to_use_sso"
                :key="'sso-' + index"
                v-html="item"
                style="font-weight: bold; color: orange"
            />
        </ul>

        <m-icon-bar
            v-on:add="showCreateTeamMemberDialog"
            v-show="can_write"
        />

        <m-settings-organization-add-team-member
            ref="add_team_member_dialog"
            v-on:ok="onAddTeamMember"
            :simple_create_team_member_dialog="simple_create_team_member_dialog"
        />

        <m-settings-organization-dialog-set-password
            ref="set_password_dialog"
            v-on:ok="onSetPassword"
        />

        <m-employee-payment-permissions-dialog
            ref="employee-payment-permissions-dialog"
            v-on:patch-employee="onPatchEmployee"
        />

        <m-employee-bulk-message-permissions
            ref="employee-bulk-message-permissions-dialog"
            v-on:patch-employee="onPatchEmployee"
        />

        <m-text-password-reset-link-dialog ref="text-password-reset-link-dialog" />
    </div>
</template>

<script>
const E164 = require('@/js/E164.js');

export default {
    props: {
        profile: {
            type: Object,
            default: function () { return {}; }
        },

        show_search_bar: {
            type: Boolean,
            default: function () { return true; }
        },

        simple_create_team_member_dialog: {
            type: Boolean,
            default: function () { return false; }
        },
    },

    data() {
        return {
            team_member_search: null,
            filter_archived: false,
            organization_sso_settings: null,
        }
    },

    methods: {
        getToggleArchiveLabel(item) {
            if (item.is_archived)
                return "Unarchive RedChirp User...";

            return "Archive RedChirp User...";
        },

        toggleArchived(item) {
            if (item.is_archived) {
                this.doToggleArchived(item);
                return;
            }

            if (!this.profile.employees.some((el) => !el.is_archived && el.is_organization_admin && el.employee_id != item.employee_id)) {
                this.$rc.alert('Unable to Archive User', 'This user can not be archived because it would leave the organization without any active organization administrator.');
                return;
            }

            this.$rc.confirm({
                'title': 'Confirm Archived User',
                'body': 'Archiving a user will deactivite their account.  Archive ' + item.username + '?',
                'btn_text_ok': 'Archive',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doToggleArchived(item);
                },
            });
        },

        doToggleArchived(item) {
            let changes = {
                'employee_id': item.employee_id,
                'is_archived': !item.is_archived
            };

            this.$emit('patch-employee', changes);
        },

        getToggleOrganizationAdminLabel(item) {
            if (item.is_organization_admin)
                return "Demote to Ordinary User...";

            return "Promote to Organization Administrator...";
        },

        getToggleSystemAdminLabel(item) {
            if (item.is_system_admin)
                return "Revoke System Admin Privledges";

            return "Make System Administrator...";
        },

        toggleSystemAdmin(item) {
            if (!item.is_system_admin) {
                this.$rc.prompt({
                    'title': 'Make ' + item.username + ' a system administrator?',
                    'text': 'Granting system administrator privledges gives a user complete access to every account in the system.<br/><br/><b>DO NOT PROCEED UNLESS YOU ARE ABSOLUTELY CERTAIN</b><br/><br/>',
                    'label': 'Type \'admin\' to confirm',
                    'after_prompt': (value) => {
                        let changes = {
                            'employee_id': item.employee_id,
                            'is_system_admin': true
                        };

                        this.$emit('patch-employee', changes);
                        this.$rc.snackbar(item.username + ' has been made a systems administrator', 3500);
                    },

                    'is_valid': (value) => {
                        if (value == null) return false;
                        return value.trim().toLowerCase() == 'admin';
                    }
                });
            }
            else {
                let changes = {
                    'employee_id': item.employee_id,
                    'is_system_admin': false
                };

                this.$emit('patch-employee', changes);
                this.$rc.snackbar('System admin privledges revoked from ' + item.username, 3500);
            }
        },

        toggleOrganizationAdmin(item) {
            let conf = confirm(item.is_organization_admin ? "Demote " + item.username + " to an ordinary user?" : "Promote " + item.username + " to an organization administrator?");

            if (conf != true)
                return;

            let changes = {
                'employee_id': item.employee_id,
                'is_organization_admin': !item.is_organization_admin
            };

            this.$emit('patch-employee', changes);
        },

        showCreateTeamMemberDialog() {
            this.$refs['add_team_member_dialog'].showDialog(this.profile.organization_id, this.profile.endpoints, this.profile.organization_account.groups);
        },


        onAddTeamMember(team_member) {
            this.$emit('add-team-member', team_member);
        },

        showSetPasswordDialog(item) {
            this.$refs['set_password_dialog'].showDialog(this.profile.organization_id, item.employee_id, item.username);
        },

        showSetBulkMessagePermissionsDialog(item) {
            this.$refs['employee-bulk-message-permissions-dialog'].showDialog(item);
        },

        showSetPaymentPermissionsDialog(item) {
            this.$refs['employee-payment-permissions-dialog'].showDialog(item);
        },

        onPatchEmployee(changes) {
            this.$emit('patch-employee', changes);
        },

        onSetPassword(new_password_data) {
            this.$emit('set-password', new_password_data);
        },

        showProfile(item) {
            this.$router.push({
                'name': 'settings-account',
                params: { 'employee_id': item.employee_id }
            }
            ).catch(e => { }); // swallow any exception
        },

        viewConversations(event, item, endpoint) {
            this.$rc.toHomeForEndpoint(this.$router, endpoint.endpoint, event.shiftKey == true);
        },

        getEmployeeEndpoints(item) {
            let employee_id = item.employee_id;
            let ret = [];
            let endpoints = this.profile.endpoints;

            for (let i = 0; i < endpoints.length; i++) {
                if (endpoints[i].assigned_to_employee_id == employee_id)
                    ret.push(endpoints[i]);
            }

            return ret;
        },

        sendPasswordResetLink(item) {
            this.$refs['text-password-reset-link-dialog'].showDialog(item.employee_id);
        },

        onClickSendWelcomeEmail(item) {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/integrations/gmail/ready-welcome',
                'timeout': 120000,

                'params': {
                    'employee_id': item.employee_id,
                },

                'on_success': (data) => {

                    if (data.error_message != null) {
                        this.$rc.alert('Unable to prepare welcome email', data.error_message);
                        return;
                    }


                    this.$rc.alert('Draft welcome email ready', 'Click <a href="' + data.draft_url + '" target="_blank">here</a> to edit and send the welcome email');
                },

                'on_error': 'Unable to prepare draft message',
            });
        },

        clear2FA(item) {
            this.$rc.confirm({
                'title': 'Confirm Reset 2FA',
                'body': 'Are you certain that you would like to reset two factor authentication (2fa) for this user?',
                'btn_text_ok': 'Reset 2FA',
                'btn_text_cancel': 'Cancel',
                'on_ok': () => {
                    this.doClear2FA(item);
                },
            });
        },

        doClear2FA(item) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/auth/2fa/disable',

                'params': {
                    'employee_id': item.employee_id,
                },

                'on_success': (data) => {
                    this.$rc.alert('Two Factor Auth Reset', 'Two Factor Authentication (2FA) has been reset for this user.<br/><br/>If the user is required to use 2FA they will be prompted to set it up again when they next sign in.');
                },

                'on_error': 'Unable to reset two factor authenication',
            });
        },

        onClickSendWelcomeEmailOrgAdmin(item) {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'PUT: /api/v1/integrations/gmail/welcome-org-admin-setting-up-organization',
                'timeout': 120000,

                'params': {
                    'employee_id': item.employee_id,
                },

                'on_success': (data) => {

                    if (data.error_message != null) {
                        this.$rc.alert('Unable to prepare welcome email', data.error_message);
                        return;
                    }


                    this.$rc.alert('Draft welcome email (for organization administrators setting up the organization) ready', 'Click <a href="' + data.draft_url + '" target="_blank">here</a> to edit and send the welcome email');
                },

                'on_error': 'Unable to prepare draft message',
            });
        },

        signInAs(item) {
            this.$rc.newSignInAs(
                {
                    'organization_id': item.organization_id,
                    'employee_id': item.employee_id,
                }
            );
        },

        onClickSetBillingSection(item) {
            this.$rc.showSpinner();

            this.$rc.rest(
                'ORG-PROFILE: /api/v1/billing/sections/:organization_id',

                {
                    'organization_id': this.profile.organization_id,
                },

                (data) => {
                    this.$rc.hideSpinner();

                    this.$rc.combo_box_prompt({
                        'title': 'Select (or enter) Billing Section',
                        'label': 'Billing Section',
                        'items': data.billing_sections,
                        'after_prompt': (value) => {
                            value = value.toLowerCase();
                            value = value.replace('_', '-');
                            value = value.replace(' ', '-');

                            let pattern = /^[a-z0-9-]+$/gm;
                            if (value.match(pattern) == null) {
                                this.$rc.alert('Invalid Billing Section', 'Billing section is not valid.  Legal characters are lower case letters, nubmers and dashes.');
                                return;
                            }



                            let changes = {
                                'employee_id': item.employee_id,
                                'billing_section': value,
                            };

                            this.$emit('patch-employee', changes);

                        }
                    });
                },

                () => {
                    this.$rc.hideSpinner();
                    this.$rc.alert('Error', 'Unable to list organization billing sections');
                },
            );


        },

        fetchSSOSettings() {
            if (!this.profile || !this.profile.organization_id) {
                return;
            }

            this.$rc.rest({
                endpoint: `GET: /api/v1/organizations/${this.profile.organization_id}/sso-status`,
                spinner: false,
                on_success: (data) => {
                    this.organization_sso_settings = data;
                },
                on_error: () => {
                    // Silently fail - SSO settings are optional
                    this.organization_sso_settings = { sso_enabled: false };
                }
            });
        },

        isUserForcedToUseSSO(item) {
            // Check if SSO is enabled for this organization and user is not a system admin
            if (!this.organization_sso_settings) {
                return false;
            }

            const sso_enabled = this.organization_sso_settings.sso_enabled;
            const is_system_admin = item.is_system_admin;

            // User is forced to use SSO if SSO is enabled and they are not a system admin
            return sso_enabled && !is_system_admin;
        },

        getPasswordResetTooltip(item) {
            if (this.isUserForcedToUseSSO(item)) {
                return 'Password management is handled through SSO';
            }
            if (item.username_used_in_other_orgs.length > 0 && !this.$rc.$state.isAuthActorSystemAdmin()) {
                return 'Org Admin Only';
            }
            return !this.$rc.$state.isAuthActorSystemAdmin() ? 'Org Admin Only' : 'Org Admin & Staff Only';
        },

        getSetPasswordTooltip(item) {
            if (this.isUserForcedToUseSSO(item)) {
                return 'Password management is handled through SSO';
            }
            if (item.username_used_in_other_orgs.length > 0 && !this.$rc.$state.isAuthActorSystemAdmin()) {
                return 'Org Admin Only';
            }
            return !this.$rc.$state.isSystemsAdmin() ? 'Org Admin Only' : 'Org Admin & Staff Only';
        },
        hasAccessToPasswordManagement(item) {
            let user_forced_to_use_sso = this.isUserForcedToUseSSO(item);
            
            //No access if user is forced to use SSO
            if (user_forced_to_use_sso) 
                return false;
            //Access if user is system admin
            if(this.$rc.$state.isAuthActorSystemAdmin()) 
                return true;

            let username_used_in_other_orgs = item.username_used_in_other_orgs.length > 0;
            if (username_used_in_other_orgs) 
                return false;
            if(this.$rc.$state.isOrganizationAdmin() && !item.is_sys_admin) 
                return true;
            
            return false;
        }
    },

    computed: {
        can_write() {
            if (this.profile == null || this.profile.actor_permissions == null) return false;
            return this.profile.actor_permissions.can_write;
        },

        is_sys_admin() {
            return this.$rc.$state.isSystemsAdmin();
        },

        team_members_headers() {
            let ret = [
                { text: 'Name', value: 'label' },
                { text: 'Username', value: 'username' },
                { text: 'Organization Admin', value: 'is_organization_admin', align: 'center' },
                { text: 'Archived', value: 'is_archived', align: 'center' },
                { text: 'Bulk Message Permissions', value: 'bulk_message_permissions' },
                { text: 'Payment Permissions', value: 'payment_permissions' },
                { text: 'Action', value: 'action', align: 'end' },
            ];

            if (this.$rc.$state.isAuthActorSystemAdmin()) {
                ret.splice(2, 0, { text: 'System Admin', value: 'is_system_admin', align: 'center' });
                ret.splice(0, 0, { text: 'Billing Section', value: 'billing_section' });

            }

            return ret;
        },

        users_that_can_access_other_orgs() {
            let employees = this.profile.employees;
            let ret = [];

            for (let i = 0; i < employees.length; i++) {
                if (employees[i].username_used_in_other_orgs.length == 0) continue;

                ret.push(employees[i].username + ' is also a user in ' + this.$rc.pretty(employees[i].username_used_in_other_orgs, 'comma_and_list'));
            }

            return ret;
        },

        users_forced_to_use_sso() {
            let employees = this.profile.employees;
            let ret = [];

            for (let i = 0; i < employees.length; i++) {
                // For now, use empty array as placeholder until backend is updated
                let sso_enforced_orgs = employees[i].sso_enforced_by_other_orgs || [];
                if (sso_enforced_orgs.length == 0) continue;

                ret.push(employees[i].username + ' is forced to use SSO due to settings in ' + this.$rc.pretty(sso_enforced_orgs, 'comma_and_list'));
            }

            return ret;
        },

        filtered_employees() {
            if (this.profile == null || this.profile.employees == null)
                return null;

            let ret = [];

            for (let i = 0; i < this.profile.employees.length; i++) {
                let cur = this.profile.employees[i];

                if (this.filter_archived == true && cur.is_archived == false)
                    continue;

                if (this.filter_archived == false && cur.is_archived == true)
                    continue;

                ret.push(cur);
            }

            return ret;
        },

    },

    watch: {
        profile: {
            handler(newProfile) {
                if (newProfile && newProfile.organization_id) {
                    this.fetchSSOSettings();
                }
            },
            immediate: true
        }
    },
}
</script>

<style lang="scss" scoped></style>